import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../context/LangContext";

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t.nav.about },
    { href: "#skills", label: t.nav.skills },
    { href: "#projects", label: t.nav.projects },
    { href: "#company", label: t.nav.company },
    { href: "#contact", label: t.nav.contact },
  ];

  const scrollTo = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 w-full z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background/95 border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a
          href="#"
          data-testid="navbar-logo"
          className="text-foreground font-semibold text-base tracking-tight"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          Pedro Lucas
        </a>

        <nav className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              className="text-muted-foreground hover:text-foreground text-sm transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="text-xs font-mono px-2.5 py-1 rounded border border-border text-muted-foreground hover:text-foreground transition-colors duration-200 uppercase"
          >
            {lang === "pt" ? "EN" : "PT"}
          </button>
          <button
            data-testid="navbar-cta"
            onClick={() => scrollTo("#contact")}
            className="bg-foreground text-background text-sm font-medium px-4 py-2 rounded-md hover:opacity-90 transition-all duration-200"
          >
            {t.nav.cta}
          </button>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggle}
            className="text-xs font-mono px-2 py-1 rounded border border-border text-muted-foreground uppercase"
          >
            {lang === "pt" ? "EN" : "PT"}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            className="text-foreground"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-t border-border overflow-hidden"
          >
            <div className="flex flex-col px-6 py-4 gap-3">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="text-muted-foreground hover:text-foreground text-sm text-left transition-colors py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("#contact")}
                className="bg-foreground text-background text-sm font-medium px-4 py-2.5 rounded-md mt-2"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
